import React, { ReactNode, createContext, useEffect, useMemo, useRef, useState } from "react";

import { IFbClient } from "@featbit/js-client-sdk";

import { FlagProject, getClient, getIdentifiedKeyId, isFeatureFlagsEnabled, isIdentifying, onIdentified, onIdentifyingChange } from "utils";

type FeatureFlagContextValue = {
  getRawClient: (project: FlagProject) => IFbClient | undefined;
  ready: Record<FlagProject, boolean>;
  version: number;
  identifiedKeyId: string | null;
  /** FeatBit is identifying a user and answers from an empty store until their flags arrive. */
  identifying: boolean;
};

const defaultContextValue: FeatureFlagContextValue = {
  getRawClient: () => undefined,
  ready: { client: false, common: false },
  version: 0,
  identifiedKeyId: null,
  identifying: false,
};

export const FeatureFlagContext = createContext<FeatureFlagContextValue>(defaultContextValue);

type Props = {
  children: ReactNode;
};

/**
 * Root-level provider that initializes the FeatBit SDK clients for every
 * configured project (`client`, `common`) and keeps consumers of
 * {@link useFeatureFlag} in sync with flag updates.
 *
 * ## Placement
 *
 * Mount this once near the top of the React tree (e.g. in `src/index.js`). Do
 * not nest multiple providers and do not place it per-component. Both portals
 * (user and admin) share the same provider.
 *
 * ## Behavior
 *
 * - Reads the SDK keys from `REACT_APP_FEATBIT_*_SDK_KEY` env vars; the server URI is a constant.
 * - When no SDK key is configured, the provider is a transparent pass-through
 *   and every flag returns its `defaultValue` with `kind: ClientNotReady`.
 * - Initialization is lazy and non-blocking: children render immediately, and
 *   flag evaluations re-run once each project's client finishes initializing.
 * - On every polling tick that produces a flag change, the provider bumps an
 *   internal `version` counter, which causes components using
 *   `useFeatureFlag` to re-evaluate.
 */
export const FeatureFlagProvider = ({ children }: Props) => {
  const clientsRef = useRef<Partial<Record<FlagProject, IFbClient>>>({});
  const [ready, setReady] = useState<Record<FlagProject, boolean>>({ client: false, common: false });
  const [version, setVersion] = useState(0);
  const [identifiedKeyId, setIdentifiedKeyId] = useState<string | null>(getIdentifiedKeyId);
  const [identifying, setIdentifying] = useState<boolean>(isIdentifying);

  useEffect(() => onIdentified(setIdentifiedKeyId), []);
  useEffect(() => onIdentifyingChange(setIdentifying), []);

  useEffect(() => {
    if (!isFeatureFlagsEnabled()) return undefined;

    let cancelled = false;
    const listeners: { client?: () => void; common?: () => void } = {};

    const projects: FlagProject[] = ["client", "common"];
    projects.forEach((project) => {
      const bumpVersion = () => {
        if (cancelled) return;
        setVersion((current) => current + 1);
      };

      getClient(project)
        .then((client) => {
          if (cancelled || !client) return;
          clientsRef.current[project] = client;
          setReady((current) => ({ ...current, [project]: true }));
          client.on("update", bumpVersion);
          listeners[project] = () => client.off("update", bumpVersion);
        })
        .catch(() => {
          // swallow — flags default to off on init failure
        });
    });

    return () => {
      cancelled = true;
      listeners.client?.();
      listeners.common?.();
    };
  }, []);

  const value = useMemo<FeatureFlagContextValue>(
    () => ({
      getRawClient: (project: FlagProject) => clientsRef.current[project],
      ready,
      version,
      identifiedKeyId,
      identifying,
    }),
    [ready, version, identifiedKeyId, identifying]
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
};
